// Range expansion helpers for array variable parser.
// Example: "1-3" -> ["1","2","3"], "A-C" -> ["A","B","C"].
import { isQuoted } from './split'

// Expand single-token range syntax when token is not quoted.
export function expandRangeToken(token: string): string[] | null {
  const trimmed = token.trim()
  // Keep quoted forms like `'A-F'` as plain text items.
  if (isQuoted(trimmed)) {
    return null
  } else {
    // Continue numeric/alphabetic range checks.
  }

  const numericRange = expandNumericRange(trimmed)
  if (numericRange) {
    return numericRange
  } else {
    // Continue alphabetic range parsing.
  }

  // Return alphabetic range or null when range token is invalid.
  return expandAlphabeticRange(trimmed)
}

// Detect mixed-unit numeric ranges like `1px-12rem`.
export function hasMismatchedNumericRangeUnits(token: string): boolean {
  const parts = parseNumericRangeParts(token)
  // Return false when token is not a numeric range form.
  if (!parts) {
    return false
  } else if (!parts.startSuffix || !parts.endSuffix) {
    // Return false when one side has no explicit unit.
    return false
  } else {
    // Return true only when both units exist and differ.
    return parts.startSuffix !== parts.endSuffix
  }
}

// Expand numeric ranges like `1-4` or `1px-4rem` (inclusive).
function expandNumericRange(token: string): string[] | null {
  const parts = parseNumericRangeParts(token)
  // Return null when token is not a numeric range expression.
  if (!parts) {
    return null
  } else {
    // Continue expanding numeric range token.
  }

  const { start, end, startSuffix, endSuffix } = parts
  const step = start <= end ? 1 : -1
  const defaultSuffix = startSuffix || endSuffix
  const values: string[] = []

  // Build inclusive sequence from start to end with step size 1.
  for (let current = start; ; current += step) {
    const suffix = resolveNumericRangeSuffix(current, start, end, startSuffix, endSuffix, defaultSuffix)
    values.push(`${current}${suffix}`)
    // Stop after pushing the end value.
    if (current === end) {
      break
    } else {
      // Continue numeric sequence expansion.
    }
  }

  // Return expanded numeric range values.
  return values
}

// Parse numeric range token parts including optional unit suffixes.
function parseNumericRangeParts(token: string): {
  start: number
  end: number
  startSuffix: string
  endSuffix: string
} | null {
  const trimmed = token.trim()
  // Keep quoted tokens out of numeric range parsing.
  if (isQuoted(trimmed)) {
    return null
  } else {
    // Continue regex parsing for unquoted tokens.
  }

  const match = trimmed.match(/^(-?\d+)([a-zA-Z%]+)?\s*-\s*(-?\d+)([a-zA-Z%]+)?$/)
  // Return null when token is not a numeric range expression.
  if (!match) {
    return null
  } else {
    // Continue numeric token part extraction.
  }

  const [, rawStart, rawStartSuffix = '', rawEnd, rawEndSuffix = ''] = match
  // Return parsed numeric range parts.
  return {
    start: Number.parseInt(rawStart, 10),
    end: Number.parseInt(rawEnd, 10),
    startSuffix: rawStartSuffix,
    endSuffix: rawEndSuffix,
  }
}

// Expand letter ranges like `A-F` or `a-f` (inclusive).
function expandAlphabeticRange(token: string): string[] | null {
  const match = token.match(/^([A-Za-z])\s*-\s*([A-Za-z])$/)
  // Return null when token is not a single-letter range expression.
  if (!match) {
    return null
  } else {
    // Continue alphabetic range expansion.
  }

  const [, startChar, endChar] = match
  const startCode = startChar.charCodeAt(0)
  const endCode = endChar.charCodeAt(0)
  const step = startCode <= endCode ? 1 : -1
  const values: string[] = []

  // Build inclusive sequence from start letter to end letter.
  for (let current = startCode; ; current += step) {
    values.push(String.fromCharCode(current))
    // Stop after pushing the end character.
    if (current === endCode) {
      break
    } else {
      // Continue alphabetic sequence expansion.
    }
  }

  // Return expanded alphabetic range values.
  return values
}

// Resolve suffix for each numeric range value including mixed-unit endpoints.
function resolveNumericRangeSuffix(
  current: number,
  start: number,
  end: number,
  startSuffix: string,
  endSuffix: string,
  defaultSuffix: string,
): string {
  // Keep the declared start suffix for the first item.
  if (current === start && startSuffix) {
    return startSuffix
  } else if (current === end && endSuffix) {
    // Keep the declared end suffix for the last item.
    return endSuffix
  } else {
    // Return default suffix for middle range items.
    return defaultSuffix
  }
}
